import { DateTime } from "luxon";
import type {
  CreditNoteStateEnum,
  InvoicePosition,
  InvoiceStateEnum,
  MonetaryFlowEnum,
  PaymentModeEnum,
  PaymentPaymentMethodsEnum,
  Type5bdEnum,
  ManualRefund,
} from "../../generated/backendClient";
import type { BackendUserRecord } from "../../providers/backendUserProvider";
import type { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import type { FundingInstructions } from "../../providers/backendUserProvider";
import type { CreditNote } from "../../providers/creditNotesProvider";
import type { Invoice } from "../../providers/invoicesProvider";
import type { Student } from "../../providers/studentsProvider";
import { formatCents } from "../../misc/Money";
import { moneyToCents } from "../../utils/moneyToCents";
import { canShowBankTransferInstructions } from "./canShowBankTransferInstructions";

type DrivingSchoolAccounts = BackendUserRecord["payload"]["accounts"][string];
type AccountTransaction = DrivingSchoolAccounts["free_credits"]["transactions"][number];
type Money = { amount: string; currency: string };
type GiftedCreditTransactionPayload = { is_revoked?: boolean };

const zeroMoney = { amount: "0", currency: "EUR" } satisfies Money;

export const studentCostOverviewItemTypes = {
  creditNote: "Gutschrift",
  giftedCredit: "Verschenktes Guthaben",
} as const;

export interface StudentCostOverviewAddress {
  name: string;
  company?: string;
  street: string;
  postalCode: string;
  city: string;
}

export interface StudentCostOverviewItem {
  id: string;
  date: DateTime;
  serviceDate?: DateTime;
  number: string;
  hasInvoiceNumber: boolean;
  type: string;
  description: string;
  status: string;
  paymentMethodLabel?: string;
  amountInCents: number;
  displayAmountInCents?: number;
  openAmountInCents: number;
  paidAmountInCents?: number;
  refundedCardPaymentInCents?: number;
  prepaidPaidAmountInCents: number;
  refundedAmountInCents?: number;
  isPrepaidRefund?: boolean;
  referencedInvoiceId?: string;
}

export interface StudentCostOverview {
  createdAt: DateTime;
  drivingSchoolName: string;
  studentName: string;
  issuerAddress: StudentCostOverviewAddress;
  recipientAddress: StudentCostOverviewAddress;
  logoUrl?: string;
  items: StudentCostOverviewItem[];
  payoutItems: StudentCostOverviewPayoutItem[];
  giftedCreditBalanceInCents: number;
  totalAmountInCents: number;
  paidAmountInCents: number;
  openAmountInCents: number;
  prepaidBalanceInCents: number;
  migratedPrepaidCreditsInCents?: number;
  openAmountAfterPrepaidBalanceInCents: number;
  bankTransfer?: {
    accountHolderName?: string;
    iban: string;
    bic?: string;
  };
}

export interface StudentCostOverviewPayoutItem {
  id: string;
  date: DateTime;
  description: string;
  amountInCents: number;
  prepaidInvoiceId?: string;
}

const invoiceTypeLabels = {
  APPLICATION_SERVICE: "Antragsservice",
  ASF_COURSE_FEE: "ASF-Kurs",
  AUTHORITY_FEE: "Prüfung",
  AUTOVIO_SUBSCRIPTION: "Autovio Abo",
  B2B: "B2B-Rechnung",
  B2B_PAYOUT_ADVANCE_RECEIPT: "B2B-Auszahlungsbeleg",
  BASE_FEE: "Grundbetrag",
  BASE_FEE_ADDITION: "Grundbetrag-Zusatz",
  CREDIT_NOTE: "Gutschrift",
  DRIVING_LESSON_INVOICE: "Fahrstunde",
  EXTERNAL_SERVICE: "Externe Leistung",
  LEGACY_B2B: "Alte B2B-Rechnung",
  LEGACY_B2C: "Alte Rechnung",
  MANUAL_REFUND_TRANSFER: "Manuelle Rueckerstattung",
  MISCELLANEOUS: "Sonstiges",
  PAYOUT_ADVANCE_RECEIPT: "Auszahlungsbeleg",
  PREPAID_CREDITS: "Vorauszahlung",
  RECEIPT_B2B: "B2B-Quittung",
  REMOTE_LESSON: "Online-Unterricht",
  TEACHING_MATERIAL: "Lehrmittel",
  THEORY_LESSON: "Theorieunterricht",
  THEORY_PRE_EXAM: "Theorie-Vorpruefung",
  THEORY_REFRESHER: "Theorie-Auffrischung",
  VR_EQUIPMENT_FEE: "VR-Ausstattung",
} satisfies Record<Type5bdEnum, string>;

const invoiceStateLabels = {
  AWAITING_PAYMENT: "Offen",
  PAID: "Bezahlt",
  PARTIALLY_PAID: "Offen",
  PARTIALLY_REFUNDED: "Offen",
  PAYMENT_REVOKED: "Offen",
  REFUNDED: "Erstattet",
  UNPAID: "Offen",
  UNPAID_WITH_PREPAID_CREDITS: "Offen",
} satisfies Record<InvoiceStateEnum, string>;

const creditNoteStateLabels = {
  AUTOVIO_PAYOUT_ITEM_CREATED: "Erstellt",
  HAS_CREDIT_NOTE_NUMBER: "Erstellt",
  HAS_POSITIONS: "In Erstellung",
  HAS_REFUND_TRANSACTION: "In Erstellung",
  IN_CREATION: "In Erstellung",
  NEW: "Neu",
  PDFRENDERED: "Erstellt",
  TRANSFER_REVERSAL_CREATED: "Erstattet",
  TRANSFER_REVERSAL_REQUIRED: "Erstattung offen",
} satisfies Record<CreditNoteStateEnum, string>;

export function buildStudentCostOverview(props: {
  student: Student;
  drivingSchool: DrivingSchool;
  invoices: Invoice[];
  creditNotes?: CreditNote[];
  manualRefunds?: ManualRefund[];
  accounts?: DrivingSchoolAccounts;
  monetaryFlow?: MonetaryFlowEnum;
  fundingInstructions?: FundingInstructions;
  createdAt?: DateTime;
}): StudentCostOverview {
  const {
    student,
    drivingSchool,
    invoices,
    creditNotes = [],
    manualRefunds = [],
    accounts,
    monetaryFlow,
    fundingInstructions,
    createdAt,
  } = props;
  const invoicesById = new Map(invoices.map((invoice) => [invoice.id, invoice]));
  const canRestoreCardPayments =
    hasNoCreditHistory(accounts) &&
    !student.balances?.migratedPrepaidCredits &&
    invoices.every(
      (invoice) =>
        invoice.payload.type !== "PREPAID_CREDITS" && moneyToCents(invoice.payload.prepaid_amount_total) === 0,
    );
  const items = [
    ...invoices.map((invoice) =>
      invoiceToOverviewItem(invoice, canRestoreCardPayments ? refundedCardPaymentInCents(invoice) : undefined),
    ),
    ...creditNotes.map((creditNote) => creditNoteToOverviewItem(creditNote, invoicesById)),
    ...giftedCreditTransactionsToOverviewItems(accounts, monetaryFlow),
  ].sort((a, b) => a.date.toMillis() - b.date.toMillis());
  const invoiceNumberedItems = items.filter((item) => item.hasInvoiceNumber);
  const openAmountInCents = items.reduce((sum, item) => sum + item.openAmountInCents, 0);
  const totalAmountInCents = invoiceNumberedItems.reduce((sum, item) => sum + item.amountInCents, 0);
  const paidAmountInCents = invoiceNumberedItems.reduce(
    (sum, item) => sum + item.amountInCents - item.openAmountInCents,
    0,
  );
  const prepaidBalanceInCents = moneyToCents(accounts?.prepaid_credits?.balance ?? zeroMoney);
  const giftedCreditBalanceInCents = giftedCreditBalance(accounts, monetaryFlow);
  const migratedPrepaidCreditsInCents = student.balances?.migratedPrepaidCredits ?? 0;
  const openAmountAfterPrepaidBalanceInCents = Math.max(openAmountInCents - prepaidBalanceInCents, 0);
  const bankTransfer =
    canShowBankTransferInstructions(student) &&
    openAmountAfterPrepaidBalanceInCents > 0 &&
    fundingInstructions?.use_bank_transfer &&
    fundingInstructions.iban
      ? {
          accountHolderName: fundingInstructions.account_holder_name,
          iban: fundingInstructions.iban,
          bic: fundingInstructions.bic,
        }
      : undefined;

  return {
    createdAt: createdAt ?? DateTime.now(),
    drivingSchoolName: drivingSchool.name,
    studentName: student.name,
    issuerAddress: drivingSchoolAddress(drivingSchool),
    recipientAddress: studentAddress(student),
    logoUrl: drivingSchool.logoUrl,
    items,
    payoutItems: manualRefunds.map(manualRefundToPayoutItem).sort((a, b) => a.date.toMillis() - b.date.toMillis()),
    giftedCreditBalanceInCents,
    totalAmountInCents,
    paidAmountInCents,
    openAmountInCents,
    prepaidBalanceInCents,
    migratedPrepaidCreditsInCents,
    openAmountAfterPrepaidBalanceInCents,
    ...(bankTransfer && { bankTransfer }),
  };
}

function giftedCreditBalance(
  accounts: DrivingSchoolAccounts | undefined,
  monetaryFlow: MonetaryFlowEnum | undefined,
): number {
  return moneyToCents(giftedCreditSource(accounts, monetaryFlow)?.balance ?? zeroMoney);
}

function giftedCreditSource(accounts: DrivingSchoolAccounts | undefined, monetaryFlow: MonetaryFlowEnum | undefined) {
  return monetaryFlow === "TRANSFER" ? accounts?.credits_contingents : accounts?.free_credits;
}

function manualRefundToPayoutItem(manualRefund: ManualRefund): StudentCostOverviewPayoutItem {
  return {
    id: manualRefund.id,
    date: DateTime.fromISO(manualRefund.created_at),
    description: `Auszahlung für Rechnung ${manualRefund.prepaid_invoice_number}`,
    amountInCents: moneyToCents(manualRefund.refund_amount),
    prepaidInvoiceId: manualRefund.prepaid_invoice,
  };
}

function hasNoCreditHistory(accounts: DrivingSchoolAccounts | undefined): boolean {
  if (!accounts) {
    return false;
  }
  return [accounts.prepaid_credits, accounts.free_credits, accounts.start_credits, accounts.credits_contingents].every(
    (account) => account?.transactions?.length === 0 && moneyToCents(account.balance) === 0,
  );
}

function refundedCardPaymentInCents(invoice: Invoice): number | undefined {
  const payment = invoice.payload.payment;
  // Refunded invoices can also be unpaid cancellations; only a refunded card payment proves receipt.
  if (
    invoice.status !== "REFUNDED" ||
    amountSign(invoice.payload.type) !== 1 ||
    !invoice.nr.trim() ||
    invoice.total <= 0 ||
    invoice.refunded_amount !== invoice.total ||
    (payment?.state !== "REFUNDED" && payment?.state !== "REFUNDED_OUTSIDE_OF_SYSTEM") ||
    payment.payment_methods.length !== 1 ||
    (payment.payment_methods[0] !== "card" && payment.payment_methods[0] !== "card_present")
  ) {
    return undefined;
  }
  return invoice.total;
}

function invoiceToOverviewItem(invoice: Invoice, refundedCardPaymentInCents?: number): StudentCostOverviewItem {
  const invoiceTotalInCents = invoiceTotalOrPositionsTotalInCents(invoice);
  const serviceAt = invoice.payload.positions.find((position) => position.service_at)?.service_at;
  return {
    id: invoice.id,
    date: invoice.createdAt,
    serviceDate: serviceAt ? DateTime.fromISO(serviceAt) : invoice.createdAt,
    number: invoice.payload.type === "PREPAID_CREDITS" ? "" : invoice.nr,
    hasInvoiceNumber: invoice.payload.type !== "PREPAID_CREDITS" && !!invoice.nr.trim(),
    type: invoiceOverviewType(invoice),
    description: invoice.payload.positions.map(positionDescription).join("; "),
    status: invoiceOverviewStatus(invoice),
    paymentMethodLabel: paymentMethodLabelForInvoice(invoice),
    amountInCents: signedAmountInCents(invoice.payload.type, invoiceTotalInCents - (invoice.refunded_amount ?? 0)),
    displayAmountInCents: invoiceDisplayAmountInCents(invoice),
    openAmountInCents: invoiceOpenAmountInCents(invoice, invoiceTotalInCents),
    paidAmountInCents: invoicePaidAmountInCents(invoice),
    refundedCardPaymentInCents,
    prepaidPaidAmountInCents: moneyToCents(invoice.payload.prepaid_amount_total),
    refundedAmountInCents: invoice.refunded_amount ?? 0,
  };
}

function invoicePaidAmountInCents(invoice: Invoice): number {
  if (invoice.status === "PARTIALLY_REFUNDED" && invoice.payload.type !== "PREPAID_CREDITS") {
    return Math.max(
      moneyToCents(invoice.payload.total_paid_gross) - moneyToCents(invoice.payload.prepaid_amount_total),
      0,
    );
  }
  return invoice.paid_amount ?? 0;
}

function invoiceDisplayAmountInCents(invoice: Invoice): number {
  if (invoice.payload.type === "PREPAID_CREDITS") {
    return Math.abs(invoice.total);
  }
  return signedAmountInCents(invoice.payload.type, invoiceTotalOrPositionsTotalInCents(invoice));
}

function invoiceTotalOrPositionsTotalInCents(invoice: Invoice): number {
  if (invoice.total !== 0) {
    return invoice.total;
  }
  const positionsTotalInCents = invoice.payload.positions.reduce(
    (sum, position) => sum + moneyToCents(position.amount_gross),
    0,
  );
  return positionsTotalInCents || invoice.total;
}

function invoiceOpenAmountInCents(invoice: Invoice, invoiceTotalInCents: number): number {
  if (invoice.payment_amount) {
    return invoice.payment_amount;
  }
  if (invoice.total === 0 && invoiceTotalInCents > 0 && invoiceStateLabels[invoice.status] === "Offen") {
    return invoiceTotalInCents;
  }
  return 0;
}

function invoiceOverviewType(invoice: Invoice): string {
  if (
    invoice.payload.type === "DRIVING_LESSON_INVOICE" &&
    invoice.payload.positions.some(
      (position) =>
        position.driving_lesson_type === "praktischePruefung" ||
        position.driving_lesson_type === "theoretischePruefung",
    )
  ) {
    return "Prüfung";
  }
  return invoiceTypeLabels[invoice.payload.type];
}

function invoiceOverviewStatus(invoice: Invoice): string {
  if (invoice.payload.type === "PREPAID_CREDITS" && invoice.status === "PAID") {
    return "Eingegangen";
  }
  return invoiceStateLabels[invoice.status];
}

function creditNoteToOverviewItem(
  creditNote: CreditNote,
  invoicesById: Map<Invoice["id"], Invoice>,
): StudentCostOverviewItem {
  return {
    id: creditNote.id,
    date: creditNote.createdAt,
    number: creditNote.nr,
    hasInvoiceNumber: false,
    type: studentCostOverviewItemTypes.creditNote,
    description: "Gutschrift",
    status: creditNoteStateLabels[creditNote.status],
    paymentMethodLabel: creditNotePaymentMethodLabel(creditNote, invoicesById),
    amountInCents: -Math.abs(creditNote.total ?? 0),
    displayAmountInCents: -Math.abs(creditNote.total ?? 0),
    openAmountInCents: 0,
    prepaidPaidAmountInCents: 0,
    isPrepaidRefund: creditNote.payload.type === "PREPAID_CREDITS",
    referencedInvoiceId: creditNote.payload.invoice,
  };
}

function creditNotePaymentMethodLabel(creditNote: CreditNote, invoicesById: Map<Invoice["id"], Invoice>): string {
  const referencedInvoice = invoicesById.get(creditNote.payload.invoice);
  const details = [
    creditNote.type,
    referencedInvoice?.nr.trim() ? `Rechnung ${referencedInvoice.nr}` : undefined,
  ].filter(Boolean);
  return details.length > 0 ? `Gutschrift (${details.join(", ")})` : "Gutschrift";
}

function giftedCreditTransactionsToOverviewItems(
  accounts: DrivingSchoolAccounts | undefined,
  monetaryFlow: MonetaryFlowEnum | undefined,
): StudentCostOverviewItem[] {
  return (giftedCreditSource(accounts, monetaryFlow)?.transactions ?? [])
    .filter((transaction) => !isRevertedOrDeletedGiftedCreditTransaction(transaction))
    .map(giftedCreditTransactionToOverviewItem);
}

function isRevertedOrDeletedGiftedCreditTransaction(transaction: AccountTransaction): boolean {
  const payload = transaction.payload as GiftedCreditTransactionPayload | undefined;
  return payload?.is_revoked === true;
}

function giftedCreditTransactionToOverviewItem(transaction: AccountTransaction): StudentCostOverviewItem {
  return {
    id: `gifted-credit-${transaction.id}`,
    date: DateTime.fromISO(transaction.created_at),
    number: "",
    hasInvoiceNumber: false,
    type: studentCostOverviewItemTypes.giftedCredit,
    description: transaction.description ?? "Verschenktes Guthaben",
    status: "Verbucht",
    paymentMethodLabel: "Guthaben",
    amountInCents: -moneyToCents(transaction.amount),
    displayAmountInCents: moneyToCents(transaction.amount),
    openAmountInCents: 0,
    prepaidPaidAmountInCents: 0,
  };
}

function paymentMethodLabelForInvoice(invoice: Invoice): string | undefined {
  const paymentMethodLabels = invoice.payload.payment?.payment_methods.map(paymentMethodLabel) ?? [];
  if (paymentMethodLabels.length > 0) {
    return paymentMethodLabels.join(", ");
  }
  return paymentModeLabel(invoice.payload.payment_mode, invoice.payload.external_payment_type);
}

function paymentMethodLabel(method: PaymentPaymentMethodsEnum): string {
  switch (method) {
    case "acss_debit":
      return "ACSS Debit";
    case "affirm":
      return "Affirm";
    case "afterpay_clearpay":
      return "Afterpay/Clearpay";
    case "alipay":
      return "Alipay";
    case "au_becs_debit":
      return "AU BECS Debit";
    case "bacs_debit":
      return "BACS Debit";
    case "bancontact":
      return "Bancontact";
    case "blik":
      return "BLIK";
    case "boleto":
      return "Boleto";
    case "card":
      return "Kreditkarte";
    case "card_present":
      return "Kartenterminal";
    case "customer_balance":
      return "Banktransfer";
    case "eps":
      return "EPS";
    case "fpx":
      return "FPX";
    case "giropay":
      return "Giropay";
    case "grabpay":
      return "GrabPay";
    case "ideal":
      return "iDEAL";
    case "interac_present":
      return "Interac";
    case "klarna":
      return "Klarna";
    case "konbini":
      return "Konbini";
    case "link":
      return "Link";
    case "oxxo":
      return "OXXO";
    case "p24":
      return "P24";
    case "paynow":
      return "PayNow";
    case "pix":
      return "Pix";
    case "promptpay":
      return "PromptPay";
    case "sepa_debit":
      return "SEPA Lastschrift";
    case "sofort":
      return "Sofort";
    case "us_bank_account":
      return "US Bank Account";
    case "wechat_pay":
      return "WeChat Pay";
    case "prepaid_credits":
      return "Guthaben";
  }
}

function paymentModeLabel(paymentMode: PaymentModeEnum | null, externalPaymentType: string | null): string | undefined {
  switch (paymentMode) {
    case "CASH":
      return externalPaymentType === "external_bank_transfer" ? "Banküberweisung" : "Barzahlung";
    case "PREPAID_CREDITS_ONLY":
      return "Guthaben";
    case "STRIPE_BANK_TRANSFER":
    case "STRIPE_CASH_BALANCE_ONLY":
      return "Banktransfer";
    case "STRIPE_CARD_TERMINAL":
      return "Kartenterminal";
    case "STRIPE_CHECKOUT_SESSION":
    case "STRIPE_CHECKOUT_SESSION_AUTH_CAPTURE":
    case "STRIPE_PAYMENT_INTENT":
    case "STRIPE_PAYMENT_INTENT_AUTH_CAPTURE":
      return "Stripe";
    case "B2B_STRIPE_TRANSFER":
      return "B2B Stripe-Transfer";
    case null:
      return undefined;
  }
}

function positionDescription(position: InvoicePosition): string {
  const { description, driving_lesson_type, driving_license_class, ue_count } = position;
  if (description === "angewendeter Rabatt" || description === "Guthaben") {
    return `${description} (${formatCents(Math.abs(moneyToCents(position.amount_gross)))})`;
  }
  if (!driving_lesson_type || !driving_license_class || !ue_count) {
    return description;
  }
  const parsedCount = parseFloat(ue_count);
  return `${driving_license_class}: ${parsedCount} x ${driving_lesson_type}`;
}

function amountSign(type: Type5bdEnum): 1 | -1 {
  return type === "CREDIT_NOTE" || type === "PREPAID_CREDITS" || type === "MANUAL_REFUND_TRANSFER" ? -1 : 1;
}

function signedAmountInCents(type: Type5bdEnum, amountInCents: number): number {
  return amountSign(type) * Math.abs(amountInCents);
}

function drivingSchoolAddress(drivingSchool: DrivingSchool): StudentCostOverviewAddress {
  return {
    name: drivingSchool.name,
    ...postalAddressFields(drivingSchool.postalAddress),
  };
}

function studentAddress(student: Student): StudentCostOverviewAddress {
  if (student.invoiceAddress) {
    return {
      name: `${student.invoiceAddress.firstName} ${student.invoiceAddress.lastName}`,
      company: student.invoiceAddress.company,
      ...postalAddressFields(student.invoiceAddress),
    };
  }
  return {
    name: student.name,
    ...postalAddressFields(student.postalAddress),
  };
}

function postalAddressFields(address: {
  street: string;
  postalCode: string;
  city: string;
}): Pick<StudentCostOverviewAddress, "street" | "postalCode" | "city"> {
  return {
    street: address.street,
    postalCode: address.postalCode,
    city: address.city,
  };
}
